import { apiClient } from './axios';

import type {
  AvailabilityDashboardData,
  CallClassification,
  CompareConfigurationsInput,
  CompareConfigurationsResult,
  ConfiguredDashboardResponse,
  DashboardConfig,
  Direction,
  PaginatedCallsResponse,
  PaginatedCapturesResponse,
  TimePreset,
} from '@repo/shared-types';

export interface GlobalFilters {
  direction: Direction;
  executedAtFrom?: string;
  executedAtTo?: string;
  timePreset: TimePreset;
  customTimeFrom?: string;
  customTimeTo?: string;
  campaignId?: string;
  callTransport?: 'PSTN' | 'WEBRTC' | 'WHATSAPP' | 'WEB_WIDGET' | 'EMAIL';
  type?: 'analytics' | 'quality';
  /** 'contacts' counts distinct contacts per classification per day. */
  mode?: 'calls' | 'contacts';
}

export const analyticsApi = {
  getClassifiedCalls: async (
    page: number,
    limit: number,
    globalFilters: GlobalFilters,
    classification: CallClassification,
    minRating?: number,
    excludeClassifications?: CallClassification[],
    metricKey?: string,
    excludeMetricKeys?: string[],
  ): Promise<PaginatedCallsResponse> => {
    const response = await apiClient.get<PaginatedCallsResponse>('/analytics/calls', {
      params: {
        page,
        limit,
        ...globalFilters,
        classification,
        minRating,
        excludeClassifications: excludeClassifications?.join(',') || undefined,
        metricKey: metricKey || undefined,
        excludeMetricKeys: excludeMetricKeys?.join(',') || undefined,
      },
    });
    return response.data;
  },

  getAvailabilityDashboard: async (filters: GlobalFilters): Promise<AvailabilityDashboardData> => {
    const response = await apiClient.get<AvailabilityDashboardData>(
      '/analytics/availability/overview',
      { params: { ...filters } },
    );
    return response.data;
  },

  getAvailabilityRecent: async (
    page: number,
    limit: number,
    filters: GlobalFilters,
  ): Promise<PaginatedCapturesResponse> => {
    const response = await apiClient.get<PaginatedCapturesResponse>(
      '/analytics/availability/recent',
      { params: { page, limit, ...filters } },
    );
    return response.data;
  },

  getConfiguredDashboard: async (filters: GlobalFilters): Promise<ConfiguredDashboardResponse> => {
    const response = await apiClient.get<ConfiguredDashboardResponse>(
      '/analytics/configured-overview',
      { params: { ...filters } },
    );
    return response.data;
  },

  getDashboardConfig: async (): Promise<DashboardConfig> => {
    const response = await apiClient.get<DashboardConfig>('/analytics/dashboard-config');
    return response.data;
  },

  compareConfigurations: async (
    input: CompareConfigurationsInput,
  ): Promise<CompareConfigurationsResult> => {
    const response = await apiClient.post<CompareConfigurationsResult>(
      '/analytics/compare-configurations',
      input,
    );
    return response.data;
  },
};
